<template>
  <div>
    <b-alert
      v-if="displayAlertSALIC"
      show
      class="bg-transparent border text-dark d-inline-flex align-items-center w-100"
    >
      <font-awesome-icon
        icon="square-check"
        size="lg"
        class="flex-shrink-0 align-self-start mt-1 text-exitos"
      />
      <div class="pl-3">
        <h4 class="text-exitos font-weight-bold mb-1">
          Concedente Apoiador via Leis de Incentivo
        </h4>
        <div class="p">
          Identificado automaticamente via
          <abbr
            title="Sistema de Apoio às Leis de Incentivo à Cultura"
            class="font-weight-bold text-exitos"
          >
            SALIC
          </abbr>
        </div>
      </div>
    </b-alert>
    <section aria-labelledby="identificacao-title" class="mb-4">
      <h3>Identificação</h3>
      <div class="border rounded-lg p-3">
        <b-form-group label="Nome" label-for="concedente-nome">
          <b-overlay :show="verificandoNome" rounded="sm">
            <b-form-input
              id="concedente-nome"
              v-model="concedente.nome"
              :state="state('nome')"
              @blur="verificarNome"
            />
          </b-overlay>
          <b-form-invalid-feedback :state="state('nome')">
            {{ error("nome") }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="CNPJ / Tax ID" label-for="concedente-tax-id">
          <b-form-input
            id="concedente-tax-id"
            v-model="concedente.tax_id"
            placeholder="EX.: 123.456.789-10 ou ABC010203AB9"
            maxlength="50"
            :state="state('tax_id')"
          />
          <small class="form-text text-muted">
            Informe o CNPJ para concedentes brasileiros ou o Tax ID equivalente
            para concedentes estrangeiros.
          </small>
          <b-form-invalid-feedback :state="state('tax_id')">
            {{ error("tax_id") }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-card
          v-if="concedentesNome.length"
          class="mb-4 shadow-lg border"
          no-body
        >
          <div class="d-flex justify-content-between align-items-center p-3">
            <div>
              <h4 class="mb-1">Confira se o concedente já está cadastrado</h4>
              <div class="text-gray">
                Caso o concedente já exista, você pode clicar para editar
              </div>
            </div>
            <b-button
              size="sm"
              variant="outline-secondary"
              @click="concedentesNome = []"
            >
              <font-awesome-icon icon="close" class="mr-1" />
              Fechar sugestões
            </b-button>
          </div>
          <b-table-lite stacked :items="concedentesNome" :fields="fields">
            <template #cell(nome)="{ item }">
              <span class="text-small text-muted"> #{{ item.id }} </span>
              <h5 class="line-clamp-1 mb-1" v-text="item.nome" />
              <div
                v-if="item?.tipo_concedente"
                class="text-small line-clamp-1 text-gray"
                v-text="item?.tipo_concedente?.nome"
              />
            </template>
            <template #cell(actions)="{ item }">
              <b-button
                :to="{ name: 'concedentes_edit', params: { id: item.id } }"
                size="sm"
                variant="outline-exitos"
              >
                <font-awesome-icon icon="pen" size="sm" class="mr-1" />
                Editar
              </b-button>
            </template>
          </b-table-lite>
        </b-card>
        <b-form-group
          label="Apresentação institucional"
          label-for="concedente-sobre"
        >
          <Editor id="concedente-sobre" v-model="concedente.sobre" />
          <small class="text-muted d-block text-right">
            {{ (concedente.sobre || "").length }} / 5.000
          </small>
          <b-form-invalid-feedback :state="state('sobre')">
            {{ error("sobre") }}
          </b-form-invalid-feedback>
        </b-form-group>
      </div>
    </section>
    <section class="mb-4" aria-labelledby="origem-title">
      <h3>Origem</h3>
      <div class="border rounded-lg p-3">
        <b-form-group label="Origem" label-for="concedente-concedente_tipo_id">
          <b-form-select
            id="concedente-concedente_tipo_id"
            v-model="concedente.concedente_tipo_id"
            :state="state('concedente_tipo_id')"
            :options="tipos"
            :disabled="loading"
          >
            <template #first>
              <b-form-select-option :value="null">
                Selecione a origem
              </b-form-select-option>
            </template>
          </b-form-select>
          <b-form-invalid-feedback :state="state('concedente_tipo_id')">
            {{ error("concedente_tipo_id") }}
          </b-form-invalid-feedback>
        </b-form-group>
      </div>
    </section>
    <section aria-labelledby="logo-title">
      <h3>Logo e site institucional</h3>
      <div class="border rounded-lg p-3">
        <b-form-group label="Imagem de logo">
          <Arquivo v-model="concedente.imagem" type="image" />
        </b-form-group>
        <b-form-group label="Site institucional" label-for="concedente-site">
          <b-form-input
            id="concedente-site"
            v-model="concedente.site"
            :state="state('site')"
          />
          <b-form-invalid-feedback :state="state('site')">
            {{ error("site") }}
          </b-form-invalid-feedback>
        </b-form-group>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, ref, watch, set } from "vue";

import services from "@/http";
import Arquivo from "@/modules/files/Components/Arquivo.vue";
import { useNotify } from "@/composables/useNotify";
import { useQuery } from "@/composables/useQuery";
import { useNamespacedActions } from "vuex-composition-helpers";
import { useConcedenteForm } from "../../composables/useConcedenteForm";
import { normalizeTaxId } from "../../helpers/normalizeTaxId";
import Editor from "@/components/Form/Editor.vue";

const { concedente, state, error, isValid, hasErrors } = useConcedenteForm([
  "nome",
  "tax_id",
  "sobre",
  "concedente_tipo_id",
  "site",
]);

const { ActionVerifyNome } = useNamespacedActions("concedentes", [
  "ActionVerifyNome",
]);

const notifications = useNotify();
const verificandoNome = ref(false);
const concedentesNome = ref([]);
const fields = [
  { key: "nome", label: "Nome", class: "" },
  { key: "actions", label: "", class: "col-1 align-middle" },
];

const { data: tipos, isFetching: tiposLoading } = useQuery({
  key: ["concedentes", "tipos"],
  queryFn: () => services.concedentes.tipos(),
  select: (response) => response.data,
  initialData: [],
});

const displayAlertSALIC = computed(
  () => !!concedente.value?.id && concedente.value?.salic?.consta === true
);

const loading = computed(() => tiposLoading.value);

async function verificarNome() {
  const { id, nome } = concedente.value;

  if (!nome.trim()) {
    concedentesNome.value = [];

    return;
  }

  verificandoNome.value = true;

  try {
    const data = await ActionVerifyNome({ id, nome });
    const sameConcedente =
      concedente.value.nome === nome && concedente.value.id === id;

    if (!sameConcedente) return;

    concedentesNome.value = data;
  } catch {
    notifications.addNotification({
      message: "Erro ao verificar nomes semelhantes.",
      type: "danger",
    });
  } finally {
    verificandoNome.value = false;
  }
}

watch(
  () => concedente.value.imagem,
  (imagem) => {
    set(concedente.value, "imagem_id", imagem?.id ?? null);
  },
  { deep: true }
);

watch(
  () => concedente.value.tax_id,
  (value) => {
    const normalized = normalizeTaxId(value);

    if (normalized !== value) {
      concedente.value.tax_id = normalized;
    }
  }
);

defineExpose({ isValid, hasErrors });
</script>
